import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea } from '../../components/mobile/ManageKit';
import { Building2, Trash2, Loader2 } from 'lucide-react';

const empty = { company_name: '', contact_name: '', phone: '', email: '', address: '', notes: '' };

const MobileManageSuppliers = () => {
  const { api, can } = useMobileAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/suppliers');
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setConfirmDel(false); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v != null)) }); setConfirmDel(false); setOpen(true); };

  const save = async () => {
    if (!(form.company_name || '').trim()) return toast.error('Company name is required');
    if (!(form.contact_name || '').trim()) return toast.error('Contact name is required');
    setSaving(true);
    try {
      const payload = { ...form, is_own_company: editing?.is_own_company || false };
      if (editing) await api.put(`/api/suppliers/${editing.id}`, payload);
      else await api.post('/api/suppliers', payload);
      toast.success(editing ? 'Supplier updated' : 'Supplier added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/suppliers/${editing.id}`);
      toast.success('Supplier deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete supplier');
    }
  };

  const filtered = items.filter((s) => s.company_name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Suppliers" subtitle={`${items.length} total`} />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search suppliers..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No suppliers" subtitle="Tap Add to create your first supplier" />
        ) : (
          <div className="space-y-2" data-testid="suppliers-list">
            {filtered.map((s) => (
              <button key={s.id} onClick={() => openEdit(s)}
                className="w-full flex items-center gap-3 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                data-testid={`supplier-row-${s.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate">{s.company_name}</div>
                  <div className="text-xs text-[var(--m-text-secondary)] truncate">{s.contact_name}{s.phone ? ` · ${s.phone}` : ''}</div>
                </div>
                {s.is_own_company && <span className="text-[10px] font-bold uppercase text-[#ffd108] bg-[#ffd108]/10 px-2 py-0.5 rounded-full">Own</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="supplier-add-fab" visible={can('suppliers.write')} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Supplier' : 'New Supplier'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Company name" required><TextInput testId="supplier-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Cranes Pty Ltd" /></Field>
            <Field label="Contact name" required><TextInput testId="supplier-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" /></Field>
            <Field label="Phone"><TextInput testId="supplier-phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0400 000 000" /></Field>
            <Field label="Email"><TextInput testId="supplier-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@acme.com" /></Field>
            <Field label="Address"><TextInput testId="supplier-address" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Notes"><TextArea testId="supplier-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="supplier-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add supplier')}
            </Button>
            {editing && !editing.is_own_company && (
              confirmDel ? (
                <Button variant="destructive" className="w-full" onClick={remove} data-testid="supplier-delete-confirm">Tap again to confirm delete</Button>
              ) : (
                <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmDel(true)} data-testid="supplier-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageSuppliers;
